const TERMINAL_PHASES = new Set(["completed", "failed", "stopped"]);

export function sanitizeArtifacts(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((artifact) => artifact && typeof artifact === "object")
    .map((artifact) => {
      const safeUrl = typeof artifact.url === "string" && /^https?:\/\//i.test(artifact.url)
        ? artifact.url
        : null;
      return {
      id: artifact.id || artifact.path || safeUrl || artifact.name || null,
      name: artifact.name || artifact.filename || null,
      type: artifact.type || artifact.mimeType || null,
      path: artifact.path || null,
      url: safeUrl,
    };
    })
    .filter((artifact) => artifact.id || artifact.name || artifact.path || artifact.url);
}

export function createInitialExecutionState(conversationId = null) {
  return {
    executionId: null,
    conversationId,
    objective: "",
    phase: "idle",
    operator: null,
    label: "Ready for a command",
    startedAt: null,
    updatedAt: null,
    completedAt: null,
    progress: [],
    actions: [],
    queue: [],
    result: null,
    error: null,
    artifacts: [],
    streamActive: false,
    harry: { active: false, stopAttempts: 0 },
  };
}

function progressItem(event, now, sequence = 0) {
  return {
    id: event.eventId || event.id || `${now}-${event.type}-${sequence}`,
    kind: event.type,
    message: event.content || "Working",
    timestamp: now,
  };
}

function upsertAction(actions, event, now) {
  if (!event.tool && !event.toolId) return actions;
  const id = event.toolId || event.tool;
  const index = actions.findIndex((action) => action.id === id);
  const previous = index >= 0 ? actions[index] : {};
  const status = event.type === "tool_queued"
    ? "queued"
    : event.type === "tool_start"
      ? "running"
      : event.success === false
        ? "failed"
        : "success";
  const nextAction = {
    ...previous,
    id,
    toolId: event.toolId || previous.toolId || null,
    name: event.tool || previous.name || "Connected action",
    status,
    input: event.type === "tool_start" ? event.input || {} : previous.input || {},
    result: event.type === "tool_complete" ? event.result ?? null : previous.result ?? null,
    error: event.type === "tool_complete" && event.success === false
      ? event.error || event.result?.error || event.content || "Action failed"
      : null,
    startedAt: event.type === "tool_start" ? now : previous.startedAt || null,
    completedAt: event.type === "tool_complete" ? now : null,
  };
  if (index < 0) return [...actions, nextAction];
  return actions.map((action, actionIndex) =>
    actionIndex === index ? nextAction : action,
  );
}

function terminalResult(event) {
  return {
    content: event.content ?? null,
    provider: event.provider || null,
    toolsUsed: Array.isArray(event.toolsUsed) ? event.toolsUsed : [],
    latency: typeof event.latency === "number" ? event.latency : null,
    roiData: event.roiData && typeof event.roiData === "object"
      ? {
          taskType: event.roiData.taskType || null,
          timeSaved: event.roiData.timeSaved ?? null,
          valueSaved: event.roiData.valueSaved ?? null,
          roi: event.roiData.roi ?? null,
        }
      : null,
    timestamp: event.timestamp || null,
  };
}

export function normalizedExecutionReducer(state, action) {
  const now = action.now || Date.now();

  if (action.type === "execution/reset") {
    return createInitialExecutionState(action.conversationId ?? null);
  }

  if (action.type === "execution/begin") {
    return {
      ...createInitialExecutionState(action.conversationId || null),
      executionId: action.executionId || `${action.conversationId || "new"}-${now}`,
      objective: action.objective || "",
      phase: "understanding",
      operator: "ollie",
      label: "Ollie is getting started",
      startedAt: now,
      updatedAt: now,
      streamActive: true,
    };
  }

  if (action.type === "execution/stopped") {
    return {
      ...state,
      phase: "stopped",
      label: action.message || "Execution stopped",
      streamActive: false,
      updatedAt: now,
      completedAt: now,
      harry: { ...state.harry, active: false },
    };
  }

  if (action.type === "execution/failed") {
    return {
      ...state,
      phase: "failed",
      label: action.message || "Execution failed",
      streamActive: false,
      updatedAt: now,
      completedAt: now,
      error: {
        message: action.message || "Execution failed",
        code: action.code || null,
        recoverable: action.recoverable !== false,
      },
      harry: { ...state.harry, active: false },
    };
  }

  if (action.type === "execution/harry_stop_attempt") {
    return {
      ...state,
      updatedAt: now,
      harry: { ...state.harry, stopAttempts: state.harry.stopAttempts + 1 },
    };
  }

  if (action.type !== "execution/event") return state;
  const event = action.event || {};

  if (TERMINAL_PHASES.has(state.phase)) return state;

  // A queued follow-up is terminal for its own HTTP request but must not
  // replace the primary execution that is already running.
  if (event.type === "queued") {
    return {
      ...state,
      updatedAt: now,
      queue: [
        ...state.queue.filter((item) => item.id !== event.messageId),
        {
          id: event.messageId,
          content: event.content || "",
          position: event.queuePosition ?? null,
          status: "queued",
          queuedAt: now,
          deliveredAt: null,
        },
      ],
    };
  }

  if (event.type === "queue_delivered") {
    return {
      ...state,
      updatedAt: now,
      queue: state.queue.map((item) =>
        item.id === event.messageId
          ? { ...item, status: "delivered", deliveredAt: now }
          : item,
      ),
    };
  }

  if (event.type === "error") {
    return normalizedExecutionReducer(state, {
      type: "execution/failed",
      message: event.content || "Execution failed",
      code: event.code,
      now,
    });
  }

  if (event.type === "complete") {
    const artifacts = sanitizeArtifacts(event.artifacts || event.result?.artifacts);
    return {
      ...state,
      conversationId: event.conversationId || state.conversationId,
      phase: "completed",
      operator: state.operator || "ollie",
      label: "Completed",
      streamActive: false,
      updatedAt: now,
      completedAt: now,
      result: terminalResult(event),
      artifacts,
      progress: [...state.progress, progressItem({ type: "complete", content: "Done" }, now, state.progress.length)],
      actions: state.actions.map((item) =>
        item.status === "running"
          ? { ...item, status: "failed", error: "Stream ended before action completed", completedAt: now }
          : item,
      ),
      harry: { ...state.harry, active: false },
    };
  }

  if (event.type === "escalation") {
    return {
      ...state,
      phase: "running",
      operator: "harry",
      label: event.content || "Harry is working",
      updatedAt: now,
      progress: [...state.progress, progressItem(event, now, state.progress.length)],
      harry: { ...state.harry, active: true },
    };
  }

  if (["tool_queued", "tool_start", "tool_complete"].includes(event.type)) {
    return {
      ...state,
      phase: "running",
      label: event.content || "Connected action running",
      updatedAt: now,
      progress: [...state.progress, progressItem(event, now, state.progress.length)],
      actions: upsertAction(state.actions, event, now),
    };
  }

  if (["status", "thinking", "executing"].includes(event.type)) {
    return {
      ...state,
      phase: event.type === "executing" ? "running" : state.phase,
      label: event.content || state.label,
      updatedAt: now,
      progress: [...state.progress, progressItem(event, now, state.progress.length)],
    };
  }

  if (["text_delta", "text", "text_end"].includes(event.type)) {
    return { ...state, phase: "running", updatedAt: now };
  }

  return state;
}

export function selectExecutionActions(execution) {
  const actions = execution?.actions || [];
  return {
    active: actions.filter((action) => ["queued", "running"].includes(action.status)),
    completed: actions.filter((action) => ["success", "failed"].includes(action.status)),
  };
}

export function selectExecutionPresentation(execution, { isOnline = true } = {}) {
  const source = execution || createInitialExecutionState();
  const status = !isOnline && ["understanding", "running"].includes(source.phase)
    ? "needs_attention"
    : source.phase;
  const label = !isOnline ? "Connection unavailable" : source.label;
  const progressSteps = (source.progress || [])
    .filter((item) => !String(item.kind).startsWith("tool_"))
    .map((item, index, items) => ({
      id: item.id,
      label: item.message,
      status:
        index === items.length - 1 && !TERMINAL_PHASES.has(source.phase)
          ? "running"
          : "success",
      timestamp: item.timestamp,
    }));
  const actionSteps = (source.actions || []).map((action) => ({
    id: action.id,
    toolId: action.toolId,
    label: action.name,
    status: action.status,
    timestamp: action.startedAt || action.completedAt,
    input: action.input,
    result: action.result,
    error: action.error,
  }));
  const steps = [...progressSteps, ...actionSteps].sort(
    (a, b) => (a.timestamp || 0) - (b.timestamp || 0),
  );

  return {
    status,
    label,
    operator: source.operator,
    currentAction: label,
    canStop: source.streamActive,
    queue: (source.queue || [])
      .filter((item) => item.status === "queued")
      .map((item) => ({ ...item, queuePosition: item.position })),
    steps,
    error: source.error || source.phase === "stopped"
      ? {
          title: status === "stopped" ? "Command stopped" : "This command needs recovery",
          message: source.error?.message || "Partial work was preserved in the conversation.",
          technicalDetail: source.error?.code || undefined,
        }
      : null,
    attention: !isOnline
      ? {
          title: "Connection unavailable",
          message: "Reconnect to continue receiving live execution updates.",
        }
      : null,
    resultSummary: source.result?.content || null,
    result: source.result,
    artifacts: source.artifacts || [],
  };
}
